<template>
  <v-responsive class="border rounded">
    <v-app>
      <v-navigation-drawer 
        v-model="drawer"
        :temporary="!isLargeScreen"
        app
        style="background-color: #FACCC7;"
      >
        <v-list>
          <!-- Toggle Switch for Canadian Content -->
          <v-list-item class="d-flex justify-center align-center">
            <v-switch v-model="isCanada" class="region-switch">
              <template #thumb>
                <font-awesome-icon v-if="isCanada" :icon="['fab', 'canadian-maple-leaf']" />
                <font-awesome-icon v-else :icon="['fas', 'flag-usa']" />
              </template>
            </v-switch>
          </v-list-item>
          <v-divider></v-divider>
          
          <!-- Theme Cards -->
          <v-list-item
            v-for="theme in filteredThemes"
            :key="theme.id"
            @click="selectTheme(theme)"
            :class="{
              'selected-theme': theme.id === selectedTheme.id,
              'theme-card': theme.id !== selectedTheme.id
            }"
          >
            <v-card class="custom-card mx-auto" outlined>
              <v-img :src="theme.thumbnail" height="100px" cover></v-img>
              <v-card-title class="text-h6">{{ theme.name }}</v-card-title>
              <v-divider></v-divider>
              <v-card-subtitle class="d-flex justify-space-between">
                {{ theme.subtitle }}
                <font-awesome-icon v-if="isCanada" :icon="['fab', 'canadian-maple-leaf']" class="ml-auto" />
                <font-awesome-icon v-else :icon="['fas', 'flag-usa']" class="ml-auto" />
              </v-card-subtitle>
            </v-card>
          </v-list-item>
        </v-list>
      </v-navigation-drawer>

      <v-app-bar class="custom-app-bar">
        <v-btn icon @click="toggleDrawer">
          <v-icon>mdi-page-layout-sidebar-left</v-icon>
        </v-btn>
        <v-breadcrumbs :items="breadcrumbItems">
          <template v-slot:prepend>
            <v-icon>
              <v-img :src="brandLogo" class="icon-img mr-2" @click="navigateHome" style="cursor: pointer;"></v-img>  
            </v-icon>
          </template>
          <template v-slot:item="{ item }">
            <v-btn text small @click="$router.push(item.to)">
              {{ item.text }}
            </v-btn>
          </template>
          <template v-slot:divider>
            <v-icon icon="mdi-chevron-right"></v-icon>
          </template>
        </v-breadcrumbs>
        <v-toolbar-title></v-toolbar-title>
        <v-btn color="primary" @click="openHelpPage" class="ml-2">
          Need Help? Reserve your Spot
        </v-btn>
      </v-app-bar>

      <v-main style="background-color: #f5f5f5;">
        <v-container>
          <!-- Return Button in the Upper Left -->
          <v-btn v-if="showGoBack" @click="$router.go(-1)" class="mb-4" outlined>
            <v-icon>mdi-arrow-left</v-icon> 
            Go Back
          </v-btn>
          <!-- Dynamically load components based on route -->
          <router-view :selectedTheme="selectedTheme"  />
        </v-container>
      </v-main>
      <VideoTour />
    </v-app>
  </v-responsive>
</template>

<script>
import { useDisplay } from 'vuetify'
import themes from './assets/themes.js';
import brandLogo from './assets/images/pink-door-logo-180.png';
import VideoTour from './components/VideoTour.vue';

export default {
  name: 'App',
  components: {
    VideoTour,
  },
  data: () => ({
    drawer: true, 
    isCanada: false,
    themes, 
    selectedTheme: 0,
    showGoBack: false,
    brandLogo,
  }),
  computed: {
     // Dynamically generate breadcrumbs from route
     breadcrumbItems() {
      const pathArray = this.$route.path.split('/').filter(path => path);

      const breadcrumbItems = pathArray.map((path, index) => {
        const to = '/' + pathArray.slice(0, index + 1).join('/');
        return {
          text: this.capitalizeFirstLetter(path), // Customize breadcrumb text
          to, // Link path
        };
      });

      // Add Home as the root breadcrumb
      breadcrumbItems.unshift({
        text: 'Home', 
        to: '/', 
      });

      return breadcrumbItems;
    },
    filteredThemes() {
      return this.themes.filter(theme => theme.region === (this.isCanada ? 'Canada' : 'USA'))
    }
  },
  created() {
    const defaultTheme = this.themes.find(theme => theme.id === 1 && theme.region === 'USA');
    this.selectedTheme = defaultTheme || this.themes[0];
  },
  watch: {
    isCanada() {
      const defaultTheme = this.themes.find(theme => theme.id === 1);
      this.selectedTheme = defaultTheme || this.filteredThemes[0];
    },
    $route(to) {
      this.updateGoBackVisibility(to);
    }
  },
  mounted() {
    // Check visibility when the component is mounted
    this.updateGoBackVisibility(this.$route);
  },
  setup() {
    const { lgAndUp } = useDisplay()
    return {
      isLargeScreen: lgAndUp,
    }
  },
  methods: {
    navigateHome() {
      this.$router.push({ name: 'Home' }); // Make sure 'home' is the name of your home route
    },
     // Capitalize first letter of breadcrumb text
     capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    updateGoBackVisibility(route) {
      // Check if the current route is PinkDoor.vue (assuming the name of the route is 'PinkDoor')
      this.showGoBack = route.name !== 'Home'; // Show the button unless the route is PinkDoor
    },
    toggleDrawer() {
      this.drawer = !this.drawer;
    },
    selectTheme(theme) {
      this.selectedTheme = theme;
    },
    openHelpPage() {
      window.open('https://link.pinkdoor.app/widget/bookings/membership-refresh', '_blank');
    },
  }
}
</script>

<style scoped>
  .icon-img {
    width: 90px; /* Set the desired width for the icon */
    height: 90px; /* Set the desired height for the icon */

  }

  .go-back-btn {
    background-color: #ffffff; /* Button background */
    color: #000000; /* Text color */
    border-radius: 4px; /* Button corner radius */
    padding: 8px 16px; /* Button padding */
    box-shadow: 0px 4px 10px rgba(248,175,166, 0.5); /* Custom shadow with red color */
    margin-left: px;
  }

  /* Optionally, override default elevation if needed */
  .go-back-btn.elevation-2 {
    box-shadow: none; /*Remove default shadow from elevation-2 */
  }

  .pink-door-primary {
    color: #FACCC7;
  }

  .pink-door-secondary {
    color: #F8AFA6;
  }

.region-switch {
  transform: scale(2);
  margin: 5px 40px;
}

.custom-card {
  width: 98%;
  margin: auto;
  background-color: #ffffff;
  color: #333;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
}

.custom-card:hover {
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.2);  
}

.v-card-title {
  padding: 16px 16px 8px;
}

.v-card-subtitle {
  padding: 4px 16px 4px;
}

.theme-card {
  cursor: pointer;
  margin-top: 5px;
  margin-bottom: 10px;
}

.selected-theme {
  background-color: #F8AFA6;
}
</style>
